import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import toast from "react-hot-toast";

interface ContentBlock {
  id: string;
  block_type: string;
  level?: number;
  content: string;
  order: number;
  metadata?: any;
}

interface BlockUpdate {
  content?: string;
  order?: number;
  metadata?: any;
}

interface NewBlock {
  block_type: string;
  level?: number;
  content: string;
  order: number;
  metadata?: any;
}

export const useContentBlocks = (articleId: number) => {
  const queryClient = useQueryClient();

  // Fetch content blocks
  const {
    data: blocks = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["content-blocks", articleId],
    queryFn: async () => {
      if (import.meta.env.DEV) {
        console.log("Fetching content blocks for article:", articleId);
      }
      const response = await api.get(
        `/api/marketing/content-blocks/?article=${articleId}`,
      );
      const data = (response.data as any).results || response.data;
      if (import.meta.env.DEV) {
        console.log("Content blocks fetched:", data?.length, "blocks");
        data?.forEach((block: ContentBlock) => {
          console.log(
            `Block ${block.id}: "${block.content?.substring(0, 50)}..."`,
          );
        });
      }
      // Sort by order
      return data.sort((a: ContentBlock, b: ContentBlock) => a.order - b.order);
    },
  });

  // Update block mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BlockUpdate }) => {
      if (import.meta.env.DEV) {
        console.log("Updating content block:", { id, data });
      }
      const response = await api.patch(
        `/api/marketing/content-blocks/${id}/`,
        data,
      );
      if (import.meta.env.DEV) {
        console.log("Content block update response:", response.data);
      }
      return response.data;
    },
    onSuccess: (data) => {
      if (import.meta.env.DEV) {
        console.log(
          "Content block update success, invalidating cache. Response:",
          data,
        );
      }
      // Force invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["content-blocks", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["article-preview", articleId],
      });
      queryClient.refetchQueries({ queryKey: ["content-blocks", articleId] });
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) {
        console.error("Failed to update content block:", error);
      }
      toast.error(
        error.response?.data?.detail || "Failed to update content block",
      );
    },
  });

  // Delete block mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/marketing/content-blocks/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-blocks", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["article-preview", articleId],
      });
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) {
        console.error("Failed to delete content block:", error);
      }
      toast.error(
        error.response?.data?.detail || "Failed to delete content block",
      );
    },
  });

  // Add block mutation
  const addMutation = useMutation({
    mutationFn: async (data: NewBlock) => {
      const response = await api.post("/api/marketing/content-blocks/", {
        ...data,
        article: articleId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-blocks", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["article-preview", articleId],
      });
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) {
        console.error("Failed to add content block:", error);
      }
      toast.error(
        error.response?.data?.detail || "Failed to add content block",
      );
    },
  });

  // Reorder blocks mutation
  const reorderMutation = useMutation({
    mutationFn: async (newOrder: ContentBlock[]) => {
      // Update order for each block
      const updates = newOrder.map((block, index) => ({
        id: block.id,
        order: index,
      }));

      const response = await api.post(
        "/api/marketing/content-blocks/reorder/",
        {
          article_id: articleId,
          blocks: updates,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-blocks", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["article-preview", articleId],
      });
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) {
        console.error("Failed to reorder content blocks:", error);
      }
      toast.error(
        error.response?.data?.detail || "Failed to reorder content blocks",
      );
    },
  });

  // Save revision mutation
  const saveRevisionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(
        `/api/marketing/articles/${articleId}/save-revision/`,
        {},
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-blocks", articleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["article-preview", articleId],
      });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({
        queryKey: ["article-revisions", articleId],
      });
    },
    onError: (error: any) => {
      if (import.meta.env.DEV) {
        console.error("Failed to save revision:", error);
      }
      toast.error(error.response?.data?.detail || "Failed to save revision");
    },
  });

  // Get revisions query
  const { data: revisions } = useQuery({
    queryKey: ["content-revisions", articleId],
    queryFn: async () => {
      const response = await api.get(
        `/api/marketing/content-blocks/revisions/?article=${articleId}`,
      );
      return (response.data as any).results || response.data;
    },
  });

  return {
    blocks,
    revisions,
    isLoading,
    updateBlock: (id: string, data: BlockUpdate) =>
      updateMutation.mutateAsync({ id, data }),
    deleteBlock: deleteMutation.mutate,
    addBlock: addMutation.mutateAsync,
    reorderBlocks: reorderMutation.mutate,
    saveRevision: saveRevisionMutation.mutateAsync,
    refetch,
  };
};
